import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Textarea } from './ui/textarea';
import { ChatCircleDots } from '@phosphor-icons/react';

function getScoreColor(score) {
    if (score === 0) return 'grade-0';
    if (score <= 2) return 'grade-1';
    if (score === 3) return 'grade-3';
    return 'grade-4';
}

function getScoreLabel(score) {
    if (score === 0) return 'Niet beoordeeld';
    if (score === 1) return 'Onvoldoende';
    if (score === 2) return 'Matig';
    if (score === 3) return 'Redelijk';
    if (score === 4) return 'Goed';
    return 'Uitstekend';
}

export default function SkillGrid({ skills, skillScores, skillNotes, categories, categoryNames, editable, onScoreChange, onNoteChange }) {
    const [hoveredSkill, setHoveredSkill] = useState(null);
    const [editingNote, setEditingNote] = useState(null);

    const handleScoreClick = (skillId, newScore) => {
        if (!editable || !onScoreChange) return;
        const currentScore = skillScores[skillId] || 0;
        const score = currentScore === newScore ? 0 : newScore;
        onScoreChange(skillId, score);
    };

    const handleNoteChange = (skillId, note) => {
        if (onNoteChange) onNoteChange(skillId, note);
    };

    return (
        <div className="space-y-8">
            {categories.map(category => {
                const categorySkills = skills.filter(s => s.category === category);
                
                return (
                    <div key={category}>
                        <h3 className="text-lg font-semibold text-white mb-4 border-b border-[#4A5568] pb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {categoryNames[category] || category}
                        </h3>
                        <div className="skill-grid">
                            {categorySkills.map(skill => {
                                const score = skillScores[skill.id] || 0;
                                const note = skillNotes?.[skill.id] || '';
                                const colorClass = getScoreColor(score);
                                const isHovered = hoveredSkill === skill.id;
                                const isEditingNote = editingNote === skill.id;
                                
                                return (
                                    <div 
                                        key={skill.id}
                                        className={`p-4 rounded-lg border-2 ${colorClass} transition-all ${editable ? 'hover:shadow-lg' : ''}`}
                                        data-testid={`skill-${skill.id}`}
                                        onMouseEnter={() => setHoveredSkill(skill.id)}
                                        onMouseLeave={() => { if (!isEditingNote) setHoveredSkill(null); }}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className="font-medium text-sm">{skill.name}</h4>
                                            <div className="flex items-center gap-1">
                                                {note && !isEditingNote && (
                                                    <ChatCircleDots size={14} className="opacity-60" />
                                                )}
                                                <div className={`score-badge text-lg font-bold rounded-full w-8 h-8 flex items-center justify-center ${colorClass}`}>
                                                    {score > 0 ? score : '-'}
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-xs opacity-70">{skill.description}</p>
                                        <div className="mt-1 text-xs font-medium opacity-60">
                                            {getScoreLabel(score)}
                                        </div>

                                        {/* Note display */}
                                        {note && !isEditingNote && (
                                            <div 
                                                className="mt-2 text-xs p-2 rounded bg-black/20 opacity-80 cursor-pointer"
                                                onClick={() => editable && setEditingNote(skill.id)}
                                            >
                                                {note}
                                            </div>
                                        )}

                                        {/* Editable score buttons */}
                                        {editable && (isHovered || score === 0) && (
                                            <div className="mt-3 pt-2 border-t border-current/20">
                                                <div className="flex items-center gap-1">
                                                    {[1, 2, 3, 4, 5].map(s => (
                                                        <button
                                                            key={s}
                                                            type="button"
                                                            onClick={() => handleScoreClick(skill.id, s)}
                                                            className={`w-7 h-7 rounded text-xs font-bold transition-all ${
                                                                score === s
                                                                    ? s <= 2
                                                                        ? 'bg-red-500 text-white ring-2 ring-red-300'
                                                                        : s === 3
                                                                            ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                                                                            : 'bg-emerald-500 text-white ring-2 ring-emerald-300'
                                                                    : 'bg-[#2D3748] border border-[#4A5568] text-gray-400 hover:border-[#D4A537] hover:text-[#D4A537]'
                                                            }`}
                                                            data-testid={`edit-skill-${skill.id}-score-${s}`}
                                                        >
                                                            {s}
                                                        </button>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditingNote(isEditingNote ? null : skill.id)}
                                                        className={`ml-1 w-7 h-7 rounded text-xs transition-all ${
                                                            isEditingNote || note
                                                                ? 'bg-[#D4A537]/20 text-[#D4A537] border border-[#D4A537]'
                                                                : 'bg-[#2D3748] border border-[#4A5568] text-gray-400 hover:border-[#D4A537]'
                                                        }`}
                                                        title="Notitie toevoegen"
                                                        data-testid={`note-toggle-${skill.id}`}
                                                    >
                                                        <ChatCircleDots size={14} className="mx-auto" />
                                                    </button>
                                                </div>
                                                
                                                {/* Note editing */}
                                                {isEditingNote && (
                                                    <Textarea
                                                        value={note}
                                                        onChange={(e) => handleNoteChange(skill.id, e.target.value)}
                                                        placeholder="Opmerking bij deze vaardigheid..."
                                                        rows={2}
                                                        className="mt-2 bg-[#1A202C] border-[#4A5568] text-white text-xs placeholder:text-gray-600 resize-none"
                                                        onBlur={() => setEditingNote(null)}
                                                        autoFocus
                                                        data-testid={`note-input-${skill.id}`}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
